import { useEffect, useRef, useState } from "react";
import { FileResponse } from "api/main/data-contracts";
import { createApi } from "util/ApiCreator";
import { FileController } from "api/main/FileController";
import { handleDownload } from "pages/processexecution/admin/ProcessExecutionAdminPage";
import { adminTheme } from "theme/adminTheme";
import { findFileType } from "./FileTypeTranslation";
import {
  Box,
  Container,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  LinearProgress,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import FolderIcon from "@mui/icons-material/Folder";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FileUploadButton from "components/button/FileUploadButton";
import { Formik } from "formik";
import * as yup from "yup";

export default function FileList() {
  const api = useRef(createApi("filestorage") as FileController);
  const [files, setFiles] = useState<FileResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    getData();
  }, []);

  async function getData() {
    setLoading(true);
    const data = await api.current.findAll();
    setFiles(data || []);
    setLoading(false);
  }

  async function handleSave() {
    if (!selectedFile) return;

    try {
      await api.current.save(
        { type: "MANUAL" },
        { uploadedFile: selectedFile },
      );
      await getData();
      setUploadDialogOpen(false);
      setSelectedFile(null);
    } catch (error) {
      console.error("Klaida įkeliant failą", error);
    }
  }

  const handleCloseDialog = () => {
    setUploadDialogOpen(false);
    setSelectedFile(null);
  };

  return (
    <Box sx={{ bgcolor: adminTheme.colors.backgroundAlt, minHeight: "100%" }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 1,
                color: adminTheme.colors.textPrimary,
              }}
            >
              Failai
            </Typography>
            <Typography
              variant="body"
              sx={{ color: adminTheme.colors.textSecondary }}
            >
              Valdykite įkeltus failus
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => setUploadDialogOpen(true)}
            sx={{
              background: adminTheme.gradients.primary,
              "&:hover": { background: adminTheme.gradients.primaryHover },
              px: 3,
              py: 1.5,
            }}
          >
            Įkelti failą
          </Button>
        </Box>

        {loading && <LinearProgress sx={{ mb: 3 }} />}

        {/* Stats Cards */}
        <Box sx={{ display: "flex", gap: 3, mb: 4, flexWrap: "wrap" }}>
          <Card
            sx={{
              flex: 1,
              minWidth: 200,
              p: 3,
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${adminTheme.colors.border}`,
              boxShadow: "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: adminTheme.colors.textMuted, mb: 1 }}
            >
              Iš viso failų
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, color: adminTheme.colors.textPrimary }}
            >
              {files.length}
            </Typography>
          </Card>
          <Card
            sx={{
              flex: 1,
              minWidth: 200,
              p: 3,
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${adminTheme.colors.border}`,
              boxShadow: "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: adminTheme.colors.textMuted, mb: 1 }}
            >
              Rankiniu būdu įkelti
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, color: adminTheme.colors.success }}
            >
              {files.filter((f) => f.type === "MANUAL").length}
            </Typography>
          </Card>
          <Card
            sx={{
              flex: 1,
              minWidth: 200,
              p: 3,
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${adminTheme.colors.border}`,
              boxShadow: "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: adminTheme.colors.textMuted, mb: 1 }}
            >
              Automatiškai sugeneruoti
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, color: adminTheme.colors.primary }}
            >
              {files.filter((f) => f.type !== "MANUAL").length}
            </Typography>
          </Card>
        </Box>

        {/* Table */}
        <Card
          sx={{
            borderRadius: adminTheme.borderRadius.lg,
            border: `1px solid ${adminTheme.colors.border}`,
            boxShadow: "none",
            overflow: "hidden",
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: adminTheme.colors.backgroundAlt,
                    "& th": {
                      fontWeight: 600,
                      color: adminTheme.colors.textPrimary,
                      borderBottom: `2px solid ${adminTheme.colors.border}`,
                      py: 2,
                    },
                  }}
                >
                  <TableCell>Pavadinimas</TableCell>
                  <TableCell>Tipas</TableCell>
                  <TableCell align="right">Veiksmai</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {files.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 6,
                          color: adminTheme.colors.textMuted,
                        }}
                      >
                        <FolderIcon
                          sx={{ fontSize: 48, mb: 2, opacity: 0.5 }}
                        />
                        <Typography>Nėra failų</Typography>
                        <Button
                          variant="outlined"
                          startIcon={<CloudUploadIcon />}
                          onClick={() => setUploadDialogOpen(true)}
                          sx={{ mt: 2 }}
                        >
                          Įkelti pirmą failą
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  files.map((file) => (
                    <TableRow
                      key={file.id}
                      sx={{
                        transition: adminTheme.transitions.fast,
                        "&:hover": {
                          bgcolor: "rgba(99, 102, 241, 0.04)",
                        },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: adminTheme.borderRadius.sm,
                              bgcolor: "rgba(99, 102, 241, 0.1)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: adminTheme.colors.primary,
                            }}
                          >
                            <FolderIcon sx={{ fontSize: 20 }} />
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: adminTheme.colors.textPrimary,
                              }}
                            >
                              {file.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: adminTheme.colors.textMuted,
                                display: "block",
                                maxWidth: 400,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {file.location}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={findFileType(file.type)?.label || file.type}
                          size="small"
                          sx={{
                            bgcolor: adminTheme.colors.backgroundAlt,
                            color: adminTheme.colors.textSecondary,
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Atsisiųsti">
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(file.id)}
                            sx={{
                              color: adminTheme.colors.primary,
                              "&:hover": {
                                bgcolor: "rgba(99, 102, 241, 0.1)",
                              },
                            }}
                          >
                            <DownloadIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Upload Dialog */}
        <Dialog
          open={uploadDialogOpen}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: adminTheme.borderRadius.lg,
            },
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: 600,
              borderBottom: `1px solid ${adminTheme.colors.border}`,
            }}
          >
            Naujo failo įkėlimas
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Formik
              initialValues={{ selectedFile: null }}
              validationSchema={yup.object({
                selectedFile: yup.mixed().required("Būtina įkelti failą."),
              })}
              onSubmit={() => handleSave()}
            >
              <FileUploadButton
                name="selectedFile"
                title="Pasirinkite failą"
                onUpload={(files: File[]) => setSelectedFile(files[0])}
              />
            </Formik>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: `1px solid ${adminTheme.colors.border}` }}>
            <Button onClick={handleCloseDialog}>Atšaukti</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!selectedFile}
              sx={{
                background: adminTheme.gradients.primary,
                "&:hover": { background: adminTheme.gradients.primaryHover },
              }}
            >
              Įkelti failą
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}
